import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, LoaderCircle, MapPin, TriangleAlert, X } from "lucide-react";
import "./DeliveryStatus.css";
import {
  DELIVERY_STATUS_FOOTER,
  DELIVERY_STATUS_TEXT,
  deliveryStatusIcon,
  updatedAgo,
} from "./deliveryStatusConstants";
import { useDeliveryStatus } from "./useDeliveryStatus";


function StatusGlyph({ status }) {
  if (status === "serviceable") return <CheckCircle2 aria-hidden="true" />;
  if (status === "checking") return <LoaderCircle className="ds-spinner" aria-hidden="true" />;
  return <TriangleAlert aria-hidden="true" />;
}


function DeliveryRow({ item }) {
  return <div className={`ds-row ds-row-${item.status}`} data-testid={`delivery-status-row-${item.platform.toLowerCase()}`}>
    <span className="ds-row-icon"><StatusGlyph status={item.status} /></span>
    <span className="ds-row-copy">
      <strong data-testid={`delivery-status-platform-${item.platform.toLowerCase()}`}>{item.platform}</strong>
      <span data-testid={`delivery-status-text-${item.platform.toLowerCase()}`}>{DELIVERY_STATUS_TEXT[item.status]}</span>
      <small data-testid={`delivery-status-updated-${item.platform.toLowerCase()}`}>{updatedAgo(item.last_checked_at)}</small>
    </span>
  </div>;
}


export const DeliveryStatusPanel = ({ pincode }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const { payload, error, loading, refresh } = useDeliveryStatus(pincode);
  const platforms = payload?.platforms || [];
  const iconState = deliveryStatusIcon(platforms, loading && !platforms.length);
  const triggerLabel = iconState === "checking" ? "Delivery status: checking" : iconState === "serviceable" ? "Delivery status: all platforms deliver" : "Delivery status needs attention";

  const TriggerIcon = useMemo(() => iconState === "checking" ? LoaderCircle : iconState === "serviceable" ? CheckCircle2 : TriangleAlert, [iconState]);

  const closePanel = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const openPanel = () => {
    setOpen(true);
    refresh();
  };

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePanel();
      if (event.key !== "Tab") return;
      const controls = [...(panelRef.current?.querySelectorAll("button:not(:disabled)") || [])];
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return <>
    <button ref={triggerRef} type="button" className={`ds-trigger ds-trigger-${iconState}`} onClick={openPanel} aria-label={triggerLabel} aria-expanded={open} aria-haspopup="dialog" data-testid="delivery-status-button">
      <TriggerIcon className={iconState === "checking" ? "ds-spinner" : ""} aria-hidden="true" />
    </button>
    {open && <div className="ds-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closePanel()} data-testid="delivery-status-backdrop">
      <section ref={panelRef} className="ds-panel" role="dialog" aria-modal="true" aria-labelledby="delivery-status-title" data-testid="delivery-status-panel">
        <header className="ds-panel-head">
          <div><h2 id="delivery-status-title" data-testid="delivery-status-title">Delivery in your area</h2><p data-testid="delivery-status-pincode"><MapPin size={14} aria-hidden="true" /> {payload?.pincode || pincode}</p></div>
          <button ref={closeRef} type="button" className="ds-close" onClick={closePanel} aria-label="Close delivery status" data-testid="delivery-status-close-button"><X aria-hidden="true" /></button>
        </header>
        <div className="ds-panel-body" aria-live="polite">
          {loading && !payload && !error && <div className="ds-loading" data-testid="delivery-status-loading"><LoaderCircle className="ds-spinner" aria-hidden="true" /> Loading delivery status…</div>}
          {error && <div className="ds-error" data-testid="delivery-status-error"><TriangleAlert aria-hidden="true" /><span>{error}</span><button type="button" onClick={refresh} data-testid="delivery-status-retry-button">Retry</button></div>}
          {!error && platforms.map((item) => <DeliveryRow key={item.platform} item={item} />)}
        </div>
        <p className="ds-footer" data-testid="delivery-status-footer">{DELIVERY_STATUS_FOOTER}</p>
      </section>
    </div>}
  </>;
};